import { type SxProps, type Theme } from "@mui/material";
import { styled, type CSSObject } from "@mui/material/styles";

import { mergeSx } from "ui/merge-sx";
import {
  ARROW_EDGE_INSET_PX,
  ARROW_OVERLAP_PX,
  ARROW_PROTRUSION_PX,
  ARROW_WIDTH_PX,
  MAX_HEIGHT_PX,
  PAPER_BORDER_WIDTH_PX,
  VIEWPORT_PADDING_PX,
} from "ui/popover/popover.constants";
import { getPlacementSide, isSidePlacement } from "ui/popover/popover.helpers";
import {
  type PopoverPlacement,
  type PopoverProps,
  type PopoverSide,
} from "ui/popover/popover.types";

import { shadows } from "design-tokens";

import Icons from "assets";

export const TRIGGER_SX: SxProps<Theme> = { display: "inline-flex" };

/**
 * `Popper` carries no z-index, where `Popover` inherits `zIndex.modal` from `Modal`. Restating it
 * keeps a non-modal panel above the sidebar (the `#36796` fix).
 */
export const POPPER_SX: SxProps<Theme> = (theme) => ({ zIndex: theme.zIndex.modal });

/**
 * Figma's `Popover` container (`15684:11423`), one reader per value. The shadow is the token rather
 * than `theme.shadows`, as in `ui/dialog`: `md` only exists there as rungs 9-24, and naming one would
 * read as an elevation choice. It replaces the rung-8 shadow MUI `Popover` gives its paper.
 *
 * The radius is a px string because a bare number in `sx` is multiplied by `shape.borderRadius`.
 */
export const PAPER_TOKENS = {
  background: (theme: Theme) => theme.palette.background.paper,
  border: (theme: Theme) => theme.palette.border.secondary,
  radius: (theme: Theme) => `${theme.shape.medium}px`,
  shadow: shadows.md,
};

const paperOutline = (theme: Theme) => `${PAPER_BORDER_WIDTH_PX}px solid ${PAPER_TOKENS.border(theme)}`;

const VIEWPORT_MAX_HEIGHT = `calc(100vh - ${2 * VIEWPORT_PADDING_PX}px)`;

/**
 * The cap keeps MUI `Popover`'s own viewport guard, `calc(100% - 32px)` against `Modal`'s full-viewport
 * root, which a plain 420px would replace. It is written against `100vh` so the `Popper` path, whose
 * paper has no sized containing block, gets the same guard. `"none"` lifts the cap but not the guard:
 * past the viewport the paper would run off-screen, so the body still scrolls there.
 */
export const getPaperMaxHeight = (maxHeight: number | "none"): string =>
  maxHeight === "none" ? VIEWPORT_MAX_HEIGHT : `min(${maxHeight}px, ${VIEWPORT_MAX_HEIGHT})`;

export const PAPER_MAX_HEIGHT = getPaperMaxHeight(MAX_HEIGHT_PX);

/** Figma's `native-browser-scroll`: a 6px `divider` thumb inside a 4px track padding. */
const SCROLLBAR_TRACK_PADDING_PX = 4;
const SCROLLBAR_THUMB_WIDTH_PX = 6;
/** Figma's pill; a string, since a bare number in `sx` is multiplied by `shape.borderRadius`. */
const SCROLLBAR_THUMB_RADIUS = "999px";

/**
 * A flex column so a child, not the paper, takes the overflow once the cap is reached. `overflow:
 * hidden` is Figma's clip, which also keeps a child's own fill inside the rounded corners. Longhands,
 * because MUI `Popover` sets `overflow-y: auto` on the paper; the arrow's later `overflow: visible`
 * shorthand still lifts both.
 *
 * The scrollbar rule reaches every scroller inside the paper — the body, or a region a call site
 * scrolls itself under its own header. It goes through `::-webkit-scrollbar` only: setting the standard
 * `scrollbar-width` or `scrollbar-color` makes Chromium ignore the WebKit pseudo-elements.
 */
export const PAPER_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "hidden",
  backgroundColor: PAPER_TOKENS.background(theme),
  border: paperOutline(theme),
  borderRadius: PAPER_TOKENS.radius(theme),
  boxShadow: PAPER_TOKENS.shadow,
  "& ::-webkit-scrollbar": {
    width: SCROLLBAR_THUMB_WIDTH_PX + 2 * SCROLLBAR_TRACK_PADDING_PX,
  },
  "& ::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.divider,
    backgroundClip: "padding-box",
    border: `${SCROLLBAR_TRACK_PADDING_PX}px solid transparent`,
    borderRadius: SCROLLBAR_THUMB_RADIUS,
  },
});

const ARROW_HEIGHT_PX = ARROW_PROTRUSION_PX + ARROW_OVERLAP_PX;

/** A side arrow is rotated about its centre, so its unrotated box sits off by half its sides' difference. */
const ARROW_SIDE_SHIFT_PX = (ARROW_WIDTH_PX - ARROW_HEIGHT_PX) / 2;

/** The paper edge facing the trigger — the one the arrow stands on. */
const ARROW_EDGE: Record<PopoverSide, PopoverSide> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

/** Degrees. The asset points down, away from a paper above it. */
const ARROW_ROTATION: Record<PopoverSide, number> = { top: 180, right: -90, bottom: 0, left: 90 };

/** Where `start` and `end` lie along each edge. */
const EDGE_ENDS: Record<PopoverSide, [string, string]> = {
  top: ["left", "right"],
  bottom: ["left", "right"],
  left: ["top", "bottom"],
  right: ["top", "bottom"],
};

/**
 * Placed from the paper's padding box, so it steps out past the 1px border; the asset's base strip then
 * lands on the border. Figma's inset is from the paper's outer corner, hence the border taken back off it.
 */
const arrowPosition = (placement: PopoverPlacement): CSSObject => {
  const edge = ARROW_EDGE[getPlacementSide(placement)];
  const sideways = edge === "left" || edge === "right";
  const shift = sideways ? ARROW_SIDE_SHIFT_PX : 0;
  const [startSide, endSide] = EDGE_ENDS[edge];
  const rotate = `rotate(${ARROW_ROTATION[edge]}deg)`;
  const inset = ARROW_EDGE_INSET_PX - PAPER_BORDER_WIDTH_PX + shift;
  const along: CSSObject = placement.endsWith("-start")
    ? { [startSide]: inset, transform: rotate }
    : placement.endsWith("-end")
      ? { [endSide]: inset, transform: rotate }
      : { [startSide]: "50%", transform: `${sideways ? "translateY" : "translateX"}(-50%) ${rotate}` };

  return { [edge]: -(ARROW_PROTRUSION_PX + PAPER_BORDER_WIDTH_PX + shift), ...along };
};

/**
 * Figma's Quick tip arrow. Its fill takes the paper's colour through `currentColor`; its second path
 * strokes the two outer sides, so the paper's outline runs on round the tip.
 */
export const PopoverArrow = styled(Icons.PopoverArrow, {
  shouldForwardProp: (prop) => prop !== "placement",
})<{ placement: PopoverPlacement }>(({ theme, placement }) => ({
  position: "absolute",
  width: ARROW_WIDTH_PX,
  height: ARROW_HEIGHT_PX,
  pointerEvents: "none",
  color: PAPER_TOKENS.background(theme),
  ...arrowPosition(placement),
  "& path:last-of-type": {
    stroke: PAPER_TOKENS.border(theme),
    strokeWidth: PAPER_BORDER_WIDTH_PX,
  },
}));

/** MUI `Popover` sets `overflow-y: auto` on the paper; the shorthand lifts both axes so the arrow shows. */
const ARROW_PAPER_SX: SxProps<Theme> = { overflow: "visible" };

/**
 * The arrow is placed from the paper's padding box, so the paper has to be its containing block. MUI
 * `Popover`'s paper is already absolutely positioned; `Popper`'s is in flow, which hands the arrow to
 * `Popper`'s root — the paper's outer edge — and shifts it 1px out on both axes. Not set on the
 * `Popover` path, where `relative` would undo MUI's own positioning.
 */
const ARROW_ANCHOR_SX: SxProps<Theme> = { position: "relative" };

type PaperSxOptions = Required<
  Pick<PopoverProps, "arrow" | "keepPageInteractive" | "placement" | "width">
> & {
  maxHeight?: PopoverProps["maxHeight"];
  /** The trigger-to-paper distance, `getPanelOffset`. */
  offset: number;
  /** The slide along the trigger that centres the arrow on it, `getArrowSkidding`. */
  skidding?: number;
};

/**
 * `Popper` takes the offset and skidding from its modifier; `Popover` has none, so margins. Always
 * `margin-top` and `margin-left`, negative above or left of the trigger: MUI places the paper by its
 * `top` and `left`, so a `margin-bottom` or `margin-right` moves nothing.
 */
const getOffsetSx = (placement: PopoverPlacement, offset: number, skidding: number): SxProps<Theme> => {
  const side = getPlacementSide(placement);
  const distance = side === "top" || side === "left" ? -offset : offset;

  return isSidePlacement(placement)
    ? { marginLeft: `${distance}px`, marginTop: `${skidding}px` }
    : { marginTop: `${distance}px`, marginLeft: `${skidding}px` };
};

export const getPaperSx = ({
  arrow,
  offset,
  skidding = 0,
  keepPageInteractive,
  maxHeight = MAX_HEIGHT_PX,
  placement,
  width,
}: PaperSxOptions): SxProps<Theme> =>
  mergeSx(
    PAPER_SX,
    { width, maxHeight: getPaperMaxHeight(maxHeight) },
    keepPageInteractive ? undefined : getOffsetSx(placement, offset, skidding),
    arrow ? ARROW_PAPER_SX : undefined,
    arrow && keepPageInteractive ? ARROW_ANCHOR_SX : undefined,
  );

/**
 * `minHeight: 0` is what lets the body shrink below its content inside the paper's flex column; without
 * it the paper clips instead of the body scrolling. The body is itself a flex column, so a call site can
 * pin part of its content — a header of its own — by letting another child shrink (`minHeight: 0`) and
 * scroll; the default `min-height: auto` leaves every other child at its content height.
 */
const BODY_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  textAlign: "center",
};

export const getBodySx = (padding: number): SxProps<Theme> => mergeSx(BODY_SX, { p: padding });
